/*
 * @Author: Claude
 * @Date: 2025-12-31
 * @Description: Grid 布局上传组件（图片、视频共用）
 */
import { Upload } from 'antd';
import { FC, ReactNode, useCallback, useEffect, useRef } from 'react';

import { useTranslate } from '@/hooks/useTranslate';
import { AIFunction } from '@/utils/const';

import { useUpload } from '../common/hooks/useUpload';
import { ExtendedUploadFile } from '../common/types';
import { FileUploadRule } from '../common/types/validator';
import { UploadConfig } from '../config/types';

const { Dragger } = Upload;

export interface GridUploadProps {
  /** 文件列表（受控模式） */
  fileList?: ExtendedUploadFile[];
  /** Form.Item 传递的 value */
  value?: ExtendedUploadFile[];
  /** 文件列表变化回调 */
  onChange?: (fileList: ExtendedUploadFile[], file?: ExtendedUploadFile) => void;
  /** 最大文件数量 */
  maxCount?: number;
  /** 校验规则 */
  rules?: FileUploadRule[];
  /** 校验失败回调 */
  onValidationFailed?: (error: string) => void;
  /** 是否开启选择模式 */
  selectable?: boolean;
  /** 选中文件变化回调 */
  onSelectChange?: (file: ExtendedUploadFile | null) => void;
  /** 提示信息 */
  tips?: string | ReactNode;
  /** 子元素（自定义上传区域） */
  children?: ReactNode;
  /** 文件类型限制 */
  accept?: string;
  /** 上传配置 */
  config: UploadConfig;
  /** 上传功能来源 */
  feature?: AIFunction;
  /** 是否启用埋点 */
  enableTracking?: boolean;
  /** 预览项渲染函数 */
  renderPreviewItem: (
    file: ExtendedUploadFile,
    onDelete: (e: React.MouseEvent) => void,
    onSelect?: () => void,
  ) => ReactNode;
  /** 上传占位符组件 */
  uploadPlaceholder?: ReactNode;
  /** 拖拽事件 */
  onDrop?: (e: React.DragEvent<HTMLDivElement>) => void;
}

/**
 * Grid 布局上传组件
 * 使用 3 列网格布局，适用于图片和视频
 */
export const GridUpload: FC<GridUploadProps> = (props) => {
  const {
    children,
    maxCount,
    onChange,
    fileList: fileListProp,
    value,
    rules,
    onValidationFailed,
    selectable = false,
    onSelectChange,
    tips,
    accept,
    config,
    feature,
    enableTracking = true,
    renderPreviewItem,
    uploadPlaceholder,
    onDrop,
    ...restProps
  } = props;

  const { t } = useTranslate();

  // 优先使用 value（Form.Item 模式），否则使用 fileList
  const fileList = value || fileListProp || [];

  // 使用 ref 保存最新的 fileList，解决批量上传的竞态问题
  const fileListRef = useRef<ExtendedUploadFile[]>(fileList);
  // 用于标记当前批次的文件选择，避免重复检查
  const currentBatchRef = useRef<File[] | null>(null);

  useEffect(() => {
    fileListRef.current = fileList;
  }, [fileList]);

  // 文件列表变化的统一处理
  const handleFileListChange = useCallback(
    (updatedFile: ExtendedUploadFile, newFileList: ExtendedUploadFile[]) => {
      // 更新 ref
      fileListRef.current = newFileList;
      // 调用外部回调
      onChange?.(newFileList);
    },
    [onChange],
  );

  // 处理选中文件（切换选中态）
  const handleSelectFile = useCallback(
    (selectedFile: ExtendedUploadFile) => {
      if (!selectable) return;

      // 将所有文件的 isSelected 设为 false，只有点击的文件为 true
      const newFileList = fileListRef.current.map((f) => ({
        ...f,
        isSelected: f.uid === selectedFile.uid,
      }));

      // 更新文件列表
      handleFileListChange(selectedFile, newFileList);

      // 通知外部选中的文件
      onSelectChange?.(selectedFile);
    },
    [selectable, handleFileListChange, onSelectChange],
  );

  // 使用统一的上传 Hook
  const { handleDeleteFile, customRequest } = useUpload({
    fileList,
    fileListRef,
    onFileListChange: handleFileListChange,
    rules,
    onValidationFailed,
    config,
    accept,
    selectable,
    onSelectChange,
    feature,
    enableTracking,
  });

  // 在文件选择后、上传前拦截
  const handleBeforeUpload = useCallback(
    (file: File, fileListToUpload: File[]) => {
      if (maxCount === undefined) return true;

      // 检查是否是新的一批文件选择
      const isNewBatch = currentBatchRef.current !== fileListToUpload;

      if (isNewBatch) {
        // 新批次，记录并检查
        currentBatchRef.current = fileListToUpload;

        // 计算选择这批文件后的总数
        const totalAfterUpload = fileListRef.current.length + fileListToUpload.length;

        if (totalAfterUpload > maxCount) {
          // 超过限制，提示并阻止整批上传
          const availableSlots = maxCount - fileListRef.current.length;
          if (availableSlots <= 0) {
            onValidationFailed?.(t('maxCount', { maxCount }));
          } else {
            onValidationFailed?.(
              t('maxCount-currentCount-allowCount', {
                maxCount,
                currentCount: fileListRef.current.length,
                allowCount: availableSlots,
              }),
            );
          }
          return false;
        }
      } else {
        // 同一批次的其他文件，检查是否超出
        const totalAfterUpload = fileListRef.current.length + fileListToUpload.length;
        if (totalAfterUpload > maxCount) {
          return false;
        }
      }

      return true; // 允许上传
    },
    [maxCount, fileListRef, onValidationFailed, t],
  );

  const hasFiles = fileList.length > 0;
  const reachedMax = maxCount !== undefined && fileList.length >= maxCount;

  return (
    <div className="w-full">
      {/* 初始状态：显示大的上传区域 */}
      {!hasFiles ? (
        <Dragger
          className="w-full"
          customRequest={customRequest}
          beforeUpload={handleBeforeUpload}
          showUploadList={false}
          multiple
          accept={accept}
          onDrop={onDrop}
          {...restProps}
        >
          {children || uploadPlaceholder}
        </Dragger>
      ) : (
        /* 有文件状态：Grid 布局（最多3列） */
        <div className="w-full grid grid-cols-3 gap-[12px] p-[8px] rounded-[8px] max-h-[312px] border-[1px] scroll-overlay border-[#dcdcdc]">
          {/* Upload 按钮（与预览项大小一致） */}
          {!reachedMax && (
            <Upload
              style={{ width: '100%' }}
              customRequest={customRequest}
              beforeUpload={handleBeforeUpload}
              showUploadList={false}
              multiple
              accept={accept}
              {...restProps}
            >
              <button className="flex flex-col justify-center items-center w-full h-[140px] rounded-[8px] bg-[#f4f5fe] text-[#4931fa] text-[14px] font-medium hover:bg-[#e8eafd] transition-colors">
                <p className="align-center leading-[18px] w-[18px] h-[18px] text-[25px] font-[300]">
                  +
                </p>
                <span>{t('merror.operate.upload')}</span>
              </button>
            </Upload>
          )}

          {/* 文件列表 */}
          {fileList.map((file) =>
            renderPreviewItem(
              file,
              (e) => {
                e.stopPropagation();
                handleDeleteFile(file);
              },
              () => handleSelectFile(file),
            ),
          )}
        </div>
      )}
    </div>
  );
};
